import { graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, Socials, Spacer, Title, CmsImage } from "@bw/bits"
import { Breadcrumbs } from "@bw/partials"
import { PageHeader, Employees, CallToAction } from "@bw/modules"
import { Footer } from "../components/modules/mapModules.jsx"
import * as Icons from "@bw/icons"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { OpeningHours, WeekDays, OpenState } from "@phoenix344/opening-hours"
import { breakpoints } from "../theme"
import parse from "html-react-parser"

const ScheduleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: bold;

  > span {
    margin: 0;
    font-weight: 300;
    font-size: 15px;
  }
`

const Logo = styled.div`
  @media (max-width: ${breakpoints.medium}px) {
    grid-row: 1 / 2;
  }
`

const Text = styled.div`
  font-weight: 300;
  font-size: 14px;
  line-height: 176.3%;
  margin-top: -3px;

  > a {
    color: var(--font-color);

    &[href^="tel"] {
      @media (min-width: ${breakpoints.medium}px) {
        text-decoration: none;
      }
    }
  }
`

const TextOpenings = styled(Text)`
  white-space: pre-wrap;
  b {
    width: 1.5em;
    display: inline-block;
  }
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  grid-gap: 25px;
  width: 100%;
  justify-content: space-between;

  .title {
    margin: 0px;
  }
`

const Itinerary = styled.a`
  display: flex;
  align-items: center;
  margin-top: 7px;
  gap: 7px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  line-height: 176.3%;
`

const StyledGrid = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 65px;
  }
`

const OpeningIndicator = ({ opened }) => {
  const { t } = useTranslation()
  const now = React.useMemo(() => new Date(), [opened])
  const isClosed = opened.getState(now) === OpenState.Closed

  return (
    <ScheduleDiv>
      {isClosed ? t("pharmacy.closed") : t("pharmacy.open")}
      {opened.isOpenSoon() === true && <span>{t("pharmacy.openSoon")}</span>}
      {opened.isClosedSoon() === true && (
        <span>{t("pharmacy.closedSoon")}</span>
      )}
    </ScheduleDiv>
  )
}

const PharmacyFormationTemplate = ({ data, pageContext, location }) => {
  const pharmacy = data.collectionPharmacies
  const mount = data.mount
  const staff = pharmacy.staff.filter(staff => staff.formation_management)
  const { t } = useTranslation()
  const url = "https://www.google.ch/maps/dir//pharmacieplus"
  const opened = React.useMemo(() => {
    const opened = new OpeningHours({
      // Predefine a date makes only sense to test the current day
      //currentDate: new Date(2020, 8, 15),
      text: {
        weekDays: [
          "<b>Di</b>",
          "<b>Lu</b>",
          "<b>Ma</b>",
          "<b>Me</b>",
          "<b>Je</b>",
          "<b>Ve</b>",
          "<b>Sa</b>",
        ],
        closed: "fermé",
        timespanSeparator: " - ",
      },
    })

    ;[
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ].forEach(day => {
      if (typeof pharmacy[day.toLowerCase()] === "undefined") {
        return false
      }

      pharmacy[day.toLowerCase()].forEach(hours => {
        if (!hours.opening || !hours.closing) {
          return false
        }
        opened.add(WeekDays[day], hours.opening, hours.closing)
      })
    })
    return opened
  }, [pharmacy])

  const networks = React.useMemo(() => {
    const networks = pharmacy.networks
    if (pharmacy.website) {
      networks.unshift({
        networkType: { value: "website" },
        networkUrl: pharmacy.website,
      })
    }
    return networks
  }, [pharmacy])

  const geolocalization = encodeURIComponent(
    `${pharmacy.title}, ${pharmacy.street}, ${pharmacy.zipCode} ${pharmacy.city}`
  )
  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: "Nos pharmacies",
      to: mount.slug,
    },
    {
      title: pharmacy.title,
      to: pharmacy.slug,
    },
    {
      title: "Formation",
      to: "#",
    },
  ]

  const mountImage = mount.header[0]?.image

  return (
    <Layout {...{ pageContext }} color={pharmacy.color?.value}>
      <Seo
        url={location.href}
        title={pharmacy.metaTitle || pharmacy.title}
        description={pharmacy.metaDescription}
        image={pharmacy.image?.url}
        meta={[
          {
            name: "robots",
            content: "noindex, nofollow",
          },
        ]}
      />
      <PageHeader
        title={pharmacy.title}
        pictogram={
          <svg className="icon">
            <use xlinkHref="/sprite-v2.svg#cross" />
          </svg>
        }
        subtitle={<OpeningIndicator opened={opened} />}
        color={pharmacy.color?.value}
        image={
          mountImage ? (
            <CmsImage src={mountImage} alt={mountImage.alt} />
          ) : undefined
        }
      />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <Section containerSize="medium">
        <StyledGrid>
          <Spacer>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.phone")} />
              <Text>
                {pharmacy.phone ? (
                  <a href={`tel:${pharmacy.phone.replaceAll(" ", "")}`}>
                    {pharmacy.phone}
                  </a>
                ) : (
                  "–"
                )}
              </Text>
            </InfoGrid>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.address")} />
              <Text>
                {pharmacy.street}
                <br />
                {pharmacy.zipCode}&nbsp;
                {pharmacy.city}
                <Itinerary
                  href={url + geolocalization}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icons.ArrowRight />
                  {t("itinerary")}
                </Itinerary>
              </Text>
            </InfoGrid>
            {networks.length > 0 && (
              <InfoGrid>
                <div />
                <div>
                  <Socials networks={networks} />
                </div>
              </InfoGrid>
            )}
          </Spacer>
          <Spacer>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.email")} />
              <Text>
                {pharmacy.email ? (
                  <a href={`mailto:${pharmacy.email}`}>{pharmacy.email}</a>
                ) : (
                  "–"
                )}
              </Text>
            </InfoGrid>
            <InfoGrid>
              <Title size="h4" level="div" title={t("pharmacy.horaires")} />
              <TextOpenings>
                <OpeningIndicator opened={opened} />
                {parse(
                  opened.toString({ locales: "fr-FR" }).replace(/\[|\]/g, "")
                )}
                {pharmacy.specialSchedules
                  ? parse(pharmacy.specialSchedules)
                  : null}
              </TextOpenings>
            </InfoGrid>
          </Spacer>
          <Logo>
            <img
              src={pharmacy.logo.url}
              alt={`Logo de la pharmacie ${pharmacy.title}`}
              height="110"
              width="auto"
            />
          </Logo>
        </StyledGrid>
      </Section>
      {staff && staff.length > 0 && (
        <Employees
          contents={staff.map(item => ({
            firstName:
              item.firstName === item.lastName ? undefined : item.firstName,
            lastName: item.lastName,
            role: (
              <>
                <p>{item.function}</p>
                <p style={{ maxWidth: "520px", marginTop: "40px" }}>
                  {t("your_contact_for_formations")}
                </p>
              </>
            ),
            active: true,
            image: item?.portrait?.url ? (
              <img
                src={item.portrait.url}
                alt={`${item.firstName} ${item.lastName}`}
              />
            ) : undefined,
            headshot: item?.portrait?.url ? (
              <img src={item.portrait.url} alt="" />
            ) : undefined,
          }))}
        />
      )}
      {pharmacy.formation_advantages && (
        <Section>
          <div
            dangerouslySetInnerHTML={{ __html: pharmacy.formation_advantages }}
          />
        </Section>
      )}
      <Section>
        <CallToAction
          title={pharmacy.title}
          subtitle={"Plus d’informations sur la pharmacie"}
          to={pharmacy.slug}
        />
      </Section>
      <Footer modules={pharmacy.footer} />
    </Layout>
  )
}

export default PharmacyFormationTemplate

export const pharmacyFormationQuery = graphql`
  query pharmacyFormationQuery($id: String!) {
    collectionPharmacies(id: { eq: $id }) {
      title
      metaTitle
      metaDescription
      image {
        url
      }
      doNotIndex
      zipCode
      website
      street
      slug
      city
      color {
        value
      }
      abilisLink
      email
      latitude
      longitude
      onlinBookingLink
      deliveryConditions
      infoSuptitle
      infoTitle
      text
      formation_advantages
      logo {
        url
      }
      staff {
        firstName
        lastName
        function
        portrait {
          url
        }
        formation_management
      }
      phone
      monday {
        opening
        closing
      }
      tuesday {
        opening
        closing
      }
      wednesday {
        opening
        closing
      }
      thursday {
        opening
        closing
      }
      saturday {
        opening
        closing
      }
      sunday {
        opening
        closing
      }
      friday {
        opening
        closing
      }
      specialSchedules
      id
      footer {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
      }
      networks {
        networkType {
          value
        }
        networkIcon {
          url
        }
        networkUrl
      }
    }
    mount: collectionPages(mount: { handle: { eq: "pharmacies" } }) {
      slug
      header {
        ... on PageHeaderModule {
          image {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 1670, quality: 80)
              }
            }
          }
        }
      }
    }
  }
`
